import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import AgentAccessManager from './AgentAccessManager';
import OperatorAccessManager from './OperatorAccessManager';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import {
  ArrowCounterClockwise,
  CalendarBlank,
  CaretDown,
  Check,
  CircleNotch,
  Envelope,
  Eye,
  Gauge,
  Lightning,
  Lock,
  Prohibit,
  Robot,
  Shield,
  TreeStructure,
  User,
  UsersThree,
  WarningCircle,
  X,
} from '@phosphor-icons/react';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', icon: Shield, color: 'text-blue-400', desc: 'Full management access' },
  { value: 'member', label: 'Member', icon: UsersThree, color: 'text-green-400', desc: 'Build & run agents' },
  { value: 'viewer', label: 'Viewer', icon: Eye, color: 'text-cyan-400', desc: 'Read-only access' },
];

const SCOPE_ICONS = {
  agent: Robot,
  orchestration: TreeStructure,
  app: Lightning,
  provider: Gauge,
  policy: Shield,
  workspace: Lock,
};

const SCOPE_LABELS = {
  agent: 'Agent',
  orchestration: 'Orchestration',
  app: 'Apps & Marketplace',
  provider: 'Providers',
  policy: 'Policy & Governance',
  workspace: 'Workspace',
};

const MemberDetailDrawer = ({ member, isOpen, onClose, onRefresh, currentUserId }) => {
  const { activeOrganization } = useOrganization();
  const [permissions, setPermissions] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (isOpen && member) {
      setActiveTab('overview');
      fetchPermissions();
      fetchActivity();
    }
  }, [isOpen, member?.id]);

  const fetchPermissions = async () => {
    if (!member) return;
    setLoadingPerms(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/team/members/${member.id}/permissions`,
        {},
        activeOrganization.organization_id
      );
      if (res.ok) {
        const data = await res.json();
        setPermissions(data);
      }
    } catch (err) {
      console.error('Failed to load permissions:', err);
    } finally {
      setLoadingPerms(false);
    }
  };

  const fetchActivity = async () => {
    if (!member) return;
    setLoadingActivity(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/team/members/${member.id}/activity`,
        {},
        activeOrganization.organization_id
      );
      if (res.ok) {
        const data = await res.json();
        setActivity(data);
      }
    } catch (err) {
      console.error('Failed to load activity:', err);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleRoleChange = async (newRole) => {
    setShowRoleDropdown(false);
    if (newRole === member.role) return;
    setChangingRole(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/team/members/${member.id}/role`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: newRole }),
        },
        activeOrganization.organization_id
      );
      if (res.ok) {
        toast.success(`Role updated to ${newRole}`);
        onRefresh();
        fetchPermissions();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update role');
      }
    } catch (err) {
      toast.error('Error updating role');
    } finally {
      setChangingRole(false);
    }
  };

  const handleStatusToggle = async () => {
    const newStatus = member.status === 'active' || !member.status ? 'disabled' : 'active';
    setChangingStatus(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/team/members/${member.id}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus }),
        },
        activeOrganization.organization_id
      );
      if (res.ok) {
        toast.success(`Member ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        onRefresh();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update status');
      }
    } catch (err) {
      toast.error('Error updating status');
    } finally {
      setChangingStatus(false);
    }
  };

  const handleResendInvite = async () => {
    setResending(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/team/invitations/${member.id}/resend`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      if (res.ok) {
        toast.success('Invitation resent');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to resend');
      }
    } catch (err) {
      toast.error('Error resending invitation');
    } finally {
      setResending(false);
    }
  };

  if (!member) return null;

  const isOwner = member.role === 'primary_admin';
  const isSelf = member.id === currentUserId;
  const canEditRole = !isOwner && !isSelf;
  const canToggleStatus = !isOwner && !isSelf && member.status !== 'invited';
  const isInvited = member.status === 'invited';

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'permissions', label: 'Permissions' },
    { id: 'access', label: 'Resource Access' },
  ];

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[540px] bg-[#0F0F0F] border-l border-[rgba(255,255,255,0.08)] p-0 overflow-hidden !top-14 !h-[calc(100%-3.5rem)]"
        data-testid="member-detail-drawer"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[rgba(255,255,255,0.08)]">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#6366F1]/20 flex items-center justify-center shrink-0">
                {member.picture ? (
                  <img src={member.picture} alt="" className="w-12 h-12 rounded-full" />
                ) : (
                  <User size={24} className="text-[#6366F1]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-white text-lg font-semibold truncate" data-testid="drawer-member-name">
                  {member.name || 'Unnamed'}
                </SheetTitle>
                <SheetDescription className="text-gray-400 text-sm truncate">
                  {member.email}
                </SheetDescription>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {getRoleBadge(member.role)}
                  {getStatusBadge(member.status)}
                  {isInvited && (
                    <button
                      onClick={handleResendInvite}
                      disabled={resending}
                      className="px-2 py-0.5 text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded hover:bg-yellow-500/20 transition-colors"
                      data-testid="resend-invite-btn"
                    >
                      {resending ? 'Sending...' : 'Resend Invite'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </SheetHeader>

          {/* Tabs */}
          <div className="flex border-b border-[rgba(255,255,255,0.08)] px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-[#6366F1]'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#6366F1]" />
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {activeTab === 'overview' && (
              <OverviewTab
                member={member}
                activity={activity}
                loadingActivity={loadingActivity}
                canEditRole={canEditRole}
                canToggleStatus={canToggleStatus}
                changingRole={changingRole}
                changingStatus={changingStatus}
                showRoleDropdown={showRoleDropdown}
                setShowRoleDropdown={setShowRoleDropdown}
                handleRoleChange={handleRoleChange}
                handleStatusToggle={handleStatusToggle}
                permissions={permissions}
              />
            )}
            {activeTab === 'permissions' && (
              <PermissionsTab
                permissions={permissions}
                loading={loadingPerms}
                canEdit={!isOwner && !isSelf}
                memberId={member.id}
                orgId={activeOrganization?.organization_id}
                onPermissionToggled={fetchPermissions}
              />
            )}
            {activeTab === 'access' && (
              <AccessTab member={member} />
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* ===== Overview Tab ===== */
const OverviewTab = ({
  member,
  activity,
  loadingActivity,
  canEditRole,
  canToggleStatus,
  changingRole,
  changingStatus,
  showRoleDropdown,
  setShowRoleDropdown,
  handleRoleChange,
  handleStatusToggle,
  permissions,
}) => (
  <>
    {/* Role & Status Controls */}
    <div className="space-y-3">
      {/* Role */}
      <div className="flex items-center justify-between p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-gray-400" />
          <span className="text-sm text-gray-400">Role</span>
        </div>
        {canEditRole ? (
          <div className="relative">
            <button
              onClick={() => setShowRoleDropdown(!showRoleDropdown)}
              disabled={changingRole}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-md text-sm text-white hover:border-[#6366F1]/50 transition-colors"
              data-testid="role-edit-btn"
            >
              {changingRole ? (
                <CircleNotch size={14} className="animate-spin" />
              ) : (
                <>
                  {formatRole(member.role)}
                  <CaretDown size={12} />
                </>
              )}
            </button>
            {showRoleDropdown && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-[#1A1A1A] border border-[rgba(255,255,255,0.1)] rounded-lg shadow-xl z-50 overflow-hidden" data-testid="role-dropdown">
                {ROLE_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const isActive = member.role === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleRoleChange(opt.value)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-[rgba(255,255,255,0.05)] transition-colors ${
                        isActive ? 'bg-[#6366F1]/10' : ''
                      }`}
                      data-testid={`role-option-${opt.value}`}
                    >
                      <Icon size={16} className={opt.color} />
                      <div className="flex-1">
                        <p className="text-white font-medium">{opt.label}</p>
                        <p className="text-xs text-gray-500">{opt.desc}</p>
                      </div>
                      {isActive && <Check size={14} className="text-[#6366F1]" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <span className="text-sm text-white">{formatRole(member.role)}</span>
        )}
      </div>

      {/* Status */}
      <div className="flex items-center justify-between p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg">
        <div className="flex items-center gap-2">
          <Lightning size={16} className="text-gray-400" />
          <span className="text-sm text-gray-400">Status</span>
        </div>
        {canToggleStatus ? (
          <button
            onClick={handleStatusToggle}
            disabled={changingStatus}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
              member.status === 'active' || !member.status
                ? 'bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20'
                : 'bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20'
            }`}
            data-testid="status-toggle-btn"
          >
            {changingStatus ? (
              <CircleNotch size={14} className="animate-spin" />
            ) : member.status === 'active' || !member.status ? (
              <>
                <Check size={14} /> Active — Click to disable
              </>
            ) : (
              <>
                <Prohibit size={14} /> Disabled — Click to enable
              </>
            )}
          </button>
        ) : (
          <span className="text-sm text-white capitalize">{member.status || 'Active'}</span>
        )}
      </div>

      {/* Seat */}
      {(member.role === 'member' || member.role === 'viewer' || member.role === 'employee') && (
        <div className="flex items-center justify-between p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg">
          <div className="flex items-center gap-2">
            <User size={16} className="text-gray-400" />
            <span className="text-sm text-gray-400">Seat</span>
          </div>
          <span className={`text-sm ${member.seat_active ? 'text-green-400' : 'text-gray-500'}`}>
            {member.seat_active ? 'Active' : 'Not assigned'}
          </span>
        </div>
      )}
    </div>

    {/* Permissions Summary */}
    {permissions && (
      <div className="p-4 bg-[rgba(99,102,241,0.05)] border border-[#6366F1]/10 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-white">Permissions</span>
          <span className="text-xs text-[#6366F1]">
            {permissions.total_permissions} of {permissions.permissions?.length || 0} enabled
          </span>
        </div>
        <div className="w-full bg-[rgba(255,255,255,0.05)] rounded-full h-1.5">
          <div
            className="bg-[#6366F1] rounded-full h-1.5 transition-all"
            style={{
              width: `${permissions.permissions?.length ? (permissions.total_permissions / permissions.permissions.length) * 100 : 0}%`,
            }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Role: <span className="text-gray-300">{permissions.rbac_role}</span>
          {permissions.grant_count > 0 && (
            <> · <span className="text-[#6366F1]">{permissions.grant_count} custom grant{permissions.grant_count > 1 ? 's' : ''}</span></>
          )}
        </p>
      </div>
    )}

    {/* Activity Summary */}
    <div>
      <h3 className="text-sm font-medium text-gray-300 mb-3">Activity</h3>
      {loadingActivity ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <CircleNotch size={14} className="animate-spin" /> Loading...
        </div>
      ) : activity ? (
        <div className="grid grid-cols-2 gap-3">
          <ActivityStat icon={Robot} label="Agents Owned" value={activity.agents_owned} />
          <ActivityStat icon={TreeStructure} label="Automations" value={activity.automations_owned} />
          <ActivityStat icon={Lightning} label="Runs (30d)" value={activity.executions_30d} />
          <ActivityStat
            icon={CalendarBlank}
            label="Last Login"
            value={activity.last_login ? formatDate(activity.last_login) : 'Never'}
            isText
          />
        </div>
      ) : (
        <p className="text-sm text-gray-500">No activity data available</p>
      )}
    </div>
  </>
);

/* ===== Permissions Tab ===== */
const PermissionsTab = ({ permissions, loading, canEdit, memberId, orgId, onPermissionToggled }) => {
  const [togglingPerm, setTogglingPerm] = useState(null);

  const handleToggle = async (permKey, currentlyAllowed) => {
    if (!canEdit) return;
    setTogglingPerm(permKey);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/team/members/${memberId}/permissions/${permKey}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: !currentlyAllowed }),
        },
        orgId
      );
      if (res.ok) {
        toast.success(`${currentlyAllowed ? 'Revoked' : 'Granted'}: ${permKey}`);
        onPermissionToggled();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update permission');
      }
    } catch (err) {
      toast.error('Error updating permission');
    } finally {
      setTogglingPerm(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircleNotch size={24} className="animate-spin text-[#6366F1]" />
      </div>
    );
  }

  if (!permissions?.grouped) {
    return <p className="text-sm text-gray-500 text-center py-8">No permission data available</p>;
  }

  const scopeOrder = ['agent', 'orchestration', 'app', 'provider', 'policy', 'workspace'];

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        {canEdit
          ? 'Click a permission to toggle it. Overrides are marked as "Custom".'
          : 'Showing permissions based on role defaults and any explicit grants.'}
      </p>
      {scopeOrder.map((scope) => {
        const perms = permissions.grouped[scope];
        if (!perms?.length) return null;
        const ScopeIcon = SCOPE_ICONS[scope] || Lock;
        const enabledCount = perms.filter((p) => p.allowed).length;
        return (
          <div key={scope} className="border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-[rgba(255,255,255,0.02)]">
              <div className="flex items-center gap-2">
                <ScopeIcon size={16} className="text-gray-400" />
                <span className="text-sm font-medium text-white">{SCOPE_LABELS[scope] || scope}</span>
              </div>
              <span className="text-xs text-gray-500">{enabledCount}/{perms.length}</span>
            </div>
            <div className="divide-y divide-[rgba(255,255,255,0.04)]">
              {perms.map((p) => {
                const isToggling = togglingPerm === p.permission;
                return (
                  <div
                    key={p.permission}
                    className={`flex items-center justify-between px-4 py-2.5 ${
                      canEdit ? 'cursor-pointer hover:bg-[rgba(255,255,255,0.03)] transition-colors' : ''
                    }`}
                    onClick={() => canEdit && !isToggling && handleToggle(p.permission, p.allowed)}
                    data-testid={`perm-toggle-${p.permission}`}
                  >
                    <div className="flex items-center gap-2">
                      {isToggling ? (
                        <CircleNotch size={14} className="animate-spin text-[#6366F1]" />
                      ) : (
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${
                            p.allowed
                              ? 'bg-green-500 border-green-500'
                              : 'bg-transparent border-gray-600'
                          } ${canEdit ? 'group-hover:border-[#6366F1]' : ''}`}
                        >
                          {p.allowed && <Check size={10} weight="bold" className="text-white" />}
                        </div>
                      )}
                      <span className={`text-sm ${p.allowed ? 'text-gray-200' : 'text-gray-500'}`}>
                        {p.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.source === 'explicit_grant' && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-[#6366F1]/10 text-[#6366F1] rounded">
                          Custom
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ===== Access Tab (reuses existing Agent/Operator managers) ===== */
const AccessTab = ({ member }) => (
  <div className="space-y-6">
    <AgentAccessManager userId={member.id} userName={member.name || member.email} userRole={member.role} />
    <OperatorAccessManager userId={member.id} userName={member.name || member.email} userRole={member.role} />
  </div>
);

/* ===== Helpers ===== */
const ActivityStat = ({ icon: Icon, label, value, isText }) => (
  <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] rounded-lg">
    <div className="flex items-center gap-1.5 mb-1">
      <Icon size={14} className="text-gray-500" />
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className={`text-sm font-medium ${isText ? 'text-gray-300' : 'text-white'}`}>
      {isText ? value : (value ?? 0)}
    </p>
  </div>
);

function formatRole(role) {
  const map = { primary_admin: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer', employee: 'Member' };
  return map[role] || role;
}

function formatDate(dateStr) {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return 'N/A';
  }
}

function getRoleBadge(role) {
  const styles = {
    primary_admin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    member: 'bg-green-500/20 text-green-400 border-green-500/30',
    viewer: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    employee: 'bg-green-500/20 text-green-400 border-green-500/30',
  };
  const labels = { primary_admin: 'OWNER', admin: 'ADMIN', member: 'MEMBER', viewer: 'VIEWER', employee: 'MEMBER' };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-medium border rounded ${styles[role] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
      {labels[role] || role?.toUpperCase()}
    </span>
  );
}

function getStatusBadge(status) {
  if (status === 'invited') {
    return (
      <span className="px-2 py-0.5 text-[10px] font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded flex items-center gap-1">
        <Envelope size={10} /> PENDING
      </span>
    );
  }
  if (status === 'disabled') {
    return (
      <span className="px-2 py-0.5 text-[10px] font-medium bg-red-500/20 text-red-400 border border-red-500/30 rounded flex items-center gap-1">
        <Prohibit size={10} /> DISABLED
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 text-[10px] font-medium bg-green-500/20 text-green-400 border border-green-500/30 rounded flex items-center gap-1">
      <Check size={10} /> ACTIVE
    </span>
  );
}

export default MemberDetailDrawer;
